/**
 * THE ENTRY PRICES THE SITE IS ALLOWED TO PUBLISH, COMPUTED BY THE ESTIMATOR ITSELF.
 *
 * WHY THIS EXISTS. On 2026-09-06 the site, the estimator and the pitch deck reps use in front of
 * prospects were quoting five different sets of numbers for the same work:
 *
 *   Website   site "from £2,500"  ·  estimator £2,500 to £9,000   ·  deck "£2k to £6k"
 *   App       site "from £8,000"  ·  estimator £17,000 to £30,000 ·  deck "£10k and up"
 *   System    site silent         ·  estimator £13,500 to £25,000 ·  deck "£6k to £20k"
 *
 * The app line was the worst of it: £8,000 is not a price this business can produce. Tick an app
 * in the estimator and the cheapest answer that exists is £10,500 for a web app and £17,000 the
 * moment a phone is involved, so a reader who believed the home page was misled inside a minute
 * by our own tool. The deck was the most expensive of it: a rep anchoring an internal tool at £6k
 * against a £13,500 floor either loses the deal or costs Tom the difference.
 *
 * THE RULE, AND IT IS THE ONLY WAY THIS STAYS FIXED: no page types a price. `estimateProject` is
 * the single source of truth, because it is the thing that actually emails a number to a
 * customer, and every published figure below is the number it really returns for the cheapest
 * honest version of that job. A price cannot drift from the quoting engine if it IS the quoting
 * engine.
 *
 * Computed at module load. `estimateProject` is pure arithmetic over a config object, so this
 * costs nothing and runs at build time on every static page.
 */

import { estimateProject } from "@/lib/estimate-project";
import type { ProjectEstimatorInputs } from "@/lib/project-estimator-types";

/** The estimator form with nothing chosen: every floor below is this plus one decision. */
const NOTHING_SELECTED: ProjectEstimatorInputs = {
  primary: "website",
  combination: { website: false, system: false, app: false },
  websiteTier: "sells",
  ecommerce: "none",
  bookingPayments: "none",
  userAccounts: false,
  adminDashboard: false,
  integrations: "none",
  platformIos: false,
  platformAndroid: false,
  platformWeb: false,
  complexity: "straightforward",
};

function floorFor(choice: Partial<ProjectEstimatorInputs>): number {
  return estimateProject({ ...NOTHING_SELECTED, ...choice }).minGbp;
}

export type PricingFloor = {
  /** Pounds, as the estimator rounds them. */
  gbp: number;
  /** "£2,500". Formatted once here so no call site invents its own punctuation. */
  label: string;
  /** What a reader gets for that figure, so the floor is never quoted without its scope. */
  scope: string;
};

function floor(gbp: number, scope: string): PricingFloor {
  return {
    gbp,
    label: new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(gbp),
    scope,
  };
}

/**
 * The published entry prices.
 *
 * `scope` DOES NOT COUNT PAGES. It used to say "up to five pages", and Tom's reply was the
 * whole problem in one line: "no one is gonna pay 2.5k for that". A page count is the easiest
 * thing in the world to compare against a freelancer and the least valuable thing about the
 * work, so quoting it invites the one comparison this business loses. What the floor actually
 * buys is `SHIP_STANDARDS` in proof.ts, rendered beside the price by `ShipStandards`.
 *
 * The shop floor is computed from an actual standard Shopify scope. A straightforward booking
 * integration is a smaller job and no longer inherits the shop price.
 */
export const PRICING_FLOORS = {
  /* Starter site. £1,500, restored 2026-09-19. Found and contacted, nothing that transacts. */
  starter: floor(
    floorFor({ primary: "website", websiteTier: "presence" }),
    "people can find you and get in touch",
  ),
  /* Business site. £2,500. Enquiries come in and you control the content. */
  website: floor(
    floorFor({ primary: "website", websiteTier: "sells" }),
    "enquiries come in, search can read it, and you control the content",
  ),
  /* A standard online shop still starts at £5,000. Simple booking is cheaper. */
  shop: floor(
    floorFor({ primary: "ecommerce", ecommerce: "standard" }),
    "a storefront with hosted checkout",
  ),
} as const;

/**
 * APPS AND SYSTEMS. £8,000+, SCOPED ON A CALL, AND DELIBERATELY NOT COMPUTED.
 *
 * Apps and internal systems remain call-scoped. This figure is a starting point, not a delivered
 * price for a full system.
 *
 * Everything else in this file is computed by the estimator, because a published figure that the
 * quoting engine disagrees with is the bug this file exists to prevent. This one is a stated price
 * instead, and the difference is the point: the two site rungs are answerable by a form, and an app
 * or an internal system is not. The number of user types, what it has to talk to, and whether a
 * phone is involved move it far enough that any range wide enough to be honest is too wide to be
 * useful. So the tool stops guessing and asks for the call.
 *
 * THE "+" AND "SCOPED ON A CALL" ARE LOAD-BEARING, NOT DECORATION. £8,000 is where this work
 * starts, not what it usually costs; the estimator's own arithmetic for a system or a phone app
 * lands well above it. Never render this figure without its scope line, and never let a page
 * imply £8,000 is a delivered price for an app. That is the exact failure recorded at the top of
 * this file, where the site said "Apps from £8,000" over an estimator whose cheapest answer was
 * £17,000, and it is only not a repeat of it because the call is now part of the offer.
 */
export const CALL_SCOPED_FLOOR = {
  gbp: 8000,
  label: "£8,000",
  /** Always rendered with the figure. */
  scope: "apps and systems, scoped on a call",
  /** For copy that needs the open-ended form inline. */
  labelPlus: "£8,000+",
} as const;

/**
 * The two figures the home page and the sector pages lead with.
 *
 * The website end is the LAUNCH rung, not the Business Website one, because a "from" price is
 * the cheapest real thing on the menu and quoting anything higher is quoting a number that is
 * not the lowest. The app end stays the MVP rung for the same reason it always was: "app" means
 * a product on a store, and there is nothing cheaper that honestly answers to that word.
 */
export const HEADLINE_FLOORS = [PRICING_FLOORS.starter, CALL_SCOPED_FLOOR] as const;

/**
 * The budget bands a visitor picks from, on the enquiry form and in the pre-call questions.
 *
 * ONE LIST, BECAUSE THERE WERE TWO AND THEY DISAGREED. The enquiry form offered "Under £15k /
 * £15k to £40k / £40k to £100k / £100k+" while the pre-call questions offered "Under £3,000 /
 * £3,000 to £10,000 / More than £10,000". The same prospect could answer both and appear to have
 * two different budgets, and neither set matched what Ownly actually charges: the form's bottom
 * band swallowed the entire website business, and the pre-call's top band began below the floor
 * for any app.
 *
 * These are QUALIFYING QUESTIONS, not a price list. They ask what the reader has, and the whole
 * value of the answer is that it separates people, so the boundaries are placed where the real
 * work divides: a website, a bigger site or small system, a system or a single-store app, and
 * everything above that. `scope` is the internal note explaining each boundary; it is not shown.
 *
 * Historic answers are unaffected. The stored value is a plain string and the label lookup falls
 * back to it, so a lead recorded under an old band still reads back.
 */
export const BUDGET_BANDS = [
  { value: "under-5k", label: "Under £5,000", scope: "a business website or simple booking" },
  { value: "5k-15k", label: "£5,000 to £15,000", scope: "a larger site, a shop, or a small system" },
  {
    value: "15k-35k",
    label: "£15,000 to £35,000",
    scope: "an internal system, or an app on one store",
  },
  {
    value: "35k-plus",
    label: "£35,000 or more",
    scope: "an app on both stores, or a multi-surface platform",
  },
  { value: "unsure", label: "Not sure yet", scope: "no signal, and that is a fine answer" },
] as const;
